/**
 * サムネイルパネル（FR-UI-05）。
 *
 * M2 ではサーバのページ画像が無いため pdf.js で生成する。コストを抑えるため、
 * (1) 表示領域に入ったものだけを (2) 一度に 1 件ずつ描画し、(3) 圏外に出たら中断する。
 * ラスタは描き写した後すぐ close し、パネルにピクセルを溜め込まない（NFR-PERF-01）。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { Translate } from '../i18n/i18n';
import type { ViewerApi } from './api';
import type { PanelContent } from './panels';

/** サムネイルの描画幅（物理ピクセル）。 */
const THUMB_WIDTH = 160;

interface Item {
  pageNo: number;
  button: HTMLButtonElement;
  canvas: HTMLCanvasElement;
  rendered: boolean;
  controller: AbortController | null;
}

export class Thumbnails implements PanelContent {
  readonly el: HTMLDivElement;

  private readonly items = new Map<Element, Item>();
  private readonly queue: Item[] = [];
  private observer: IntersectionObserver | null = null;
  private running = false;
  private disposed = false;

  constructor(
    doc: Document,
    private readonly registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly translate: Translate,
    pageCount: number,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('thumbs');

    for (let pageNo = 1; pageNo <= pageCount; pageNo += 1) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = cls('thumb');
      button.setAttribute(
        'aria-label',
        translate('page.label', { label: api.pageLabel(pageNo) }),
      );
      const canvas = doc.createElement('canvas');
      // 既定の 300x150 のピクセルバッファを持たせない（未描画は 0 とわかるようにする）。
      canvas.width = 0;
      canvas.height = 0;
      const caption = doc.createElement('span');
      caption.className = cls('thumb-label');
      caption.textContent = api.pageLabel(pageNo);
      button.append(canvas, caption);

      registry.listen(button, 'click', () => this.api.goto(pageNo));
      this.el.appendChild(button);
      this.items.set(button, { pageNo, button, canvas, rendered: false, controller: null });
    }
  }

  /** パネルが開いたときに監視を始める（閉じたままなら 1 枚も描かない）。 */
  activate(): void {
    if (this.observer || this.disposed) return;
    // 監視の基準はビューポート（root: null）。パネルのスクロールで隠れた項目は
    // 祖先のはみ出しクリップにより非交差と判定されるため、見えている分だけが描画される。
    this.observer = this.registry.observer(
      new IntersectionObserver((entries) => this.onIntersect(entries), {
        rootMargin: '200px 0px',
      }),
    );
    for (const item of this.items.values()) this.observer.observe(item.button);
  }

  /** 現在ページを選択状態にする。 */
  update(pageNo: number): void {
    for (const item of this.items.values()) {
      item.button.setAttribute('aria-current', String(item.pageNo === pageNo));
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const item of this.items.values()) item.controller?.abort();
    this.items.clear();
    this.queue.length = 0;
    this.el.remove();
  }

  private onIntersect(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const item = this.items.get(entry.target);
      if (!item) continue;
      if (entry.isIntersecting) {
        if (!item.rendered && !this.queue.includes(item)) this.queue.push(item);
      } else {
        // 圏外に出たものは描画待ちから外し、進行中なら中断する。
        const index = this.queue.indexOf(item);
        if (index >= 0) this.queue.splice(index, 1);
        item.controller?.abort();
        item.controller = null;
      }
    }
    void this.drain();
  }

  /** 直列に 1 件ずつ描く（同時レンダで pdf.js を詰まらせない）。 */
  private async drain(): Promise<void> {
    if (this.running || this.disposed) return;
    this.running = true;
    try {
      let item = this.queue.shift();
      while (item && !this.disposed) {
        await this.render(item);
        item = this.queue.shift();
      }
    } finally {
      this.running = false;
    }
  }

  private async render(item: Item): Promise<void> {
    if (item.rendered) return;
    const controller = new AbortController();
    item.controller = controller;
    try {
      const raster = await this.api.thumbnail(item.pageNo, THUMB_WIDTH, controller.signal);
      if (!controller.signal.aborted) {
        item.canvas.width = raster.width;
        item.canvas.height = raster.height;
        item.canvas.getContext('2d')?.drawImage(raster.bitmap, 0, 0);
        item.rendered = true;
      }
      // 描き写したら即座に手放す（サムネイル分のピクセルを二重に持たない）。
      raster.close();
    } catch {
      // 中断・失敗は次の交差で再試行される。
    } finally {
      item.controller = null;
    }
  }
}
